import { SaveOutlined } from '@ant-design/icons';
import { Col, Form, Input, message, Modal, Row, Space } from 'antd';
import React, { useState } from 'react';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { ModalWidth, VALIDATE_REQUIRED } from '@/config';
import { RegionModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
import BoxTypeInput from '@/components/FormItems/shipment/BoxTypeInput';
import CreateBtn from '@/components/app/CreateBtn';
import ExportBtn from '@/components/app/ExportBtn';
import ExportColumnsSelectorModal from '@/components/modals/ExportColumnsSelectorModal';
import { exportService } from '@/services/exportService';

const columns: IColumnType<RegionModel>[] = [
  {
    title: 'base.Name',
    dataIndex: 'name',
    sorter: true,
  },
  {
    title: 'shipmentManagementPage_BoxType',
    dataIndex: 'boxTypeName',
  },
];

const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'name', label: 'base.Name' }];

const BoxCategoryTab = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showExportModal, toggleExportModal] = React.useState<boolean>(false);
  const [exportColumns, setExportColumns] = React.useState<string[]>([]);

  React.useEffect(() => {
    apiSdk.BoxCategoryService.getExportColumns().then(res => {
      setExportColumns(res);
    });
  }, []);
  const createData = values => {
    setLoading(true);
    apiSdk.BoxCategoryService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateData = values => {
    setLoading(true);
    apiSdk.BoxCategoryService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const onExportExcel = (cols: string[]) => {
    exportService
      .exportBoxCategory({
        ...(zTb.current?.getFilter() || {}),
        columns: cols,
      })
      .toExcel();
  };
  const onExportPdf = (cols: string[]) => {
    exportService
      .exportBoxCategory({
        ...(zTb.current?.getFilter() || {}),
        columns: cols,
      })
      .toPdf();
  };

  return (
    <div>
      <ZTable
        columns={columns}
        primaryKey="id"
        tableRef={zTb}
        url="/shipments/box-categories/get-box-categories"
        bordered
        order_by="name"
        order_by_asc={true}
        allowEdit={true}
        onEdit={record => {
          setIsNewForm(false);
          setFormVisible(true);
          form.setFieldsValue({
            ...record,
          });
        }}
        allowDelete={true}
        delete_url="/shipments/box-categories/delete"
        filters={filters}
        searchBtnAlignLeft
        extraBtns={
          <Space>
            <ExportBtn
              disabled={!exportColumns?.length}
              onClick={() => {
                toggleExportModal(true);
              }}
            />
            <CreateBtn
              onClick={() => {
                setIsNewForm(true);
                setFormVisible(true);
                form.resetFields();
              }}
            />
          </Space>
        }
      ></ZTable>
      <Modal
        visible={formVisible}
        maskClosable={false}
        width={ModalWidth.small}
        className="nta-custom-form"
        title={isNewForm ? t('shipmentManagementPage_CreateBoxCategory') : t('shipmentManagementPage_UpdateBoxCategory')}
        forceRender={true}
        okText={t('save')}
        cancelText={t('cancel')}
        okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
        onCancel={() => {
          form.resetFields();
          setFormVisible(false);
        }}
        onOk={() => {
          form
            .validateFields()
            .then(values => {
              if (values.id) {
                updateData(values);
              } else {
                createData(values);
              }
            })
            .catch(info => {});
        }}
      >
        <Form form={form} size="middle" layout="vertical">
          <Form.Item name="id" className="hidden">
            <Input type="hidden" />
          </Form.Item>
          <Row gutter={6}>
            <Col md={24}>
              <Form.Item label={t('base.Name')} name="name" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col md={24}>
              <Form.Item label={t('shipmentManagementPage_BoxType')} name="boxTypeId" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                <BoxTypeInput />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
      {showExportModal ? (
        <ExportColumnsSelectorModal
          onClose={() => toggleExportModal(false)}
          columns={exportColumns}
          onExportExcel={onExportExcel}
          onExportPdf={onExportPdf}
        />
      ) : null}
    </div>
  );
};
export default BoxCategoryTab;
